/** Copy variants for Student vs School audience modes, brand tone preserved */

/**
 * Image URLs (replace with your CDN later). Same Unsplash query pattern as cdnImages.js.
 */
const u = (path, w = 1600) =>
  `https://images.unsplash.com/${path}?ixlib=rb-4.0.3&auto=format&fit=crop&w=${w}&q=80`;

/** Same-origin hero art for Hostinger/static hosting (files in `public/marketing/`). */
const marketing = (filename) => `${process.env.PUBLIC_URL || ''}/marketing/${filename}`;

const IMG = {
  solutionStudent: u('photo-1434030216411-0b793f4b4173'),
  solutionSchool: u('photo-1544531586-fde5298cdd40'),
  credibilityStudent: u('photo-1532094349884-543bc11b234d', 1400),
  credibilitySchool: u('photo-1524178232363-1fb2b075b655', 1400),
  trustStudent: u('photo-1509062522246-3755977927d7'),
  /** Trust strip (schools): partners & leadership collaboration (verified Unsplash 200) */
  trustSchool: u('photo-1523580846011-d3a5bc25702b'),
  howStudent: u('photo-1456513080510-7bf3a84b82f8'),
  howSchool: u('photo-1497633762265-9d179a990aa6'),
};

export const heroContent = {
  student: {
    eyebrow: 'Clarity today. Confidence for life.',
    titleMain: 'Some students\nalready know',
    titleAccent: '.',
    subtitle: "Not because they're smarter.\nBecause they figured it out earlier.",
    primaryCta: 'Know your Career DNA',
    secondaryCta: 'See How It Works',
    featureBar: [
      {
        icon: 'UserRound',
        title: 'Personalized assessment',
        description: 'Scientifically designed flow to understand how you think, work, and decide.',
      },
      {
        icon: 'FileText',
        title: 'Clear Career DNA report',
        description: 'Plain-language results you can read with family and revisit anytime.',
      },
      {
        icon: 'UsersRound',
        title: 'Paths that fit you',
        description: 'Options grounded in your patterns—not only the most talked-about careers.',
      },
    ],
    heroImageSrc: marketing('hero-student.jpg'),
    heroImageFallback: u('photo-1434030216411-0b793f4b4173', 1600),
    heroImageAlt: 'Student reflecting at a desk with a laptop',
  },
  school: {
    eyebrow: 'For schools',
    titleMain: 'Some Schools.\nGuide Better',
    titleAccent: '.',
    subtitle: 'Not because they teach more.\nBecause they help students choose right.',
    primaryCta: 'Partner With Us',
    secondaryCta: 'See How It Works',
    featureBar: [
      {
        icon: 'Users',
        title: 'Student Impact',
        description: 'Help every student discover their right path.',
      },
      {
        icon: 'BarChart3',
        title: 'Data-Driven Insights',
        description: 'Make informed decisions with science-backed reports.',
      },
      {
        icon: 'Shield',
        title: 'Trusted Partnership',
        description: 'End-to-end support for schools, educators & parents.',
      },
    ],
    heroTrust: {
      eyebrow: 'Trusted by forward-thinking schools',
      schools: [
        { name: 'Delhi Public School', city: 'Bangalore' },
        { name: 'The Shri Ram School', city: 'Moulsari' },
        { name: 'Sanskriti School', city: 'Pune' },
        { name: 'Oakridge International', city: 'Hyderabad' },
        { name: 'Indus International School', city: 'Bangalore' },
      ],
    },
    heroImageSrc: marketing('hero-school.jpg'),
    heroImageFallback: u('photo-1524178232363-1fb2b075b655', 1600),
    heroImageAlt: 'Educator with students in a bright learning space',
  },
};

export const trustStripContent = {
  student: {
    eyebrow: 'Loved by students & families across India',
    logos: [
      { abbr: '9 to 12', name: 'Class 9 to 12', sub: 'Right time to explore' },
      { abbr: 'IN', name: 'Across India', sub: 'Learners & families' },
      { abbr: 'DNA', name: 'Career DNA', sub: 'Structured framework' },
      { abbr: 'PDF', name: 'Clear reports', sub: 'Share with family' },
      { abbr: 'AI', name: 'AI + experts', sub: 'Guidance layer' },
    ],
    metrics: [
      { value: '3891+', label: 'Career reports generated', delay: 0, border: 'blue' },
      { value: '12+', label: 'School & program partners', delay: 120, startFrom: 3, border: 'teal' },
      { value: '4.8/5', label: 'Avg. satisfaction', delay: 240, startFrom: 3.2, border: 'blue' },
      { value: '5+', label: 'Cities represented', delay: 360, startFrom: 1, border: 'teal' },
    ],
    bannerImage: {
      src: IMG.trustStudent,
      alt: 'Students learning together in a bright classroom',
    },
  },
  school: {
    eyebrow: 'Trusted by progressive schools & partners',
    logos: [
      { abbr: 'DPS', name: 'Delhi Public School', sub: 'Partner school' },
      { abbr: 'GIIS', name: 'Global Indian Int. School', sub: 'Pilot program' },
      { abbr: 'NHV', name: 'New Horizon Academy', sub: 'Career week' },
      { abbr: 'SV', name: 'Silver Valley School', sub: 'Bulk assessment' },
      { abbr: 'EK', name: 'EduKraft Institute', sub: 'Counselling partner' },
    ],
    metrics: [
      { value: '840+', label: 'Reports delivered to schools', delay: 0, border: 'blue' },
      { value: '42+', label: 'Leadership conversations', delay: 120, startFrom: 12, border: 'teal' },
      { value: '4.8/5', label: 'Program satisfaction', delay: 240, startFrom: 3.2, border: 'blue' },
      { value: '5+', label: 'States & regions', delay: 360, startFrom: 4, border: 'teal' },
    ],
    bannerImage: {
      src: IMG.trustSchool,
      alt: 'School leaders and partners planning career programs together',
    },
  },
};

export const credibilityContent = {
  student: {
    badge: 'Built on serious research, written for you',
    title: 'Big choices feel lighter when you understand your own patterns first.',
    body:
      'We studied how young people think, learn, and decide under pressure, then turned that into a simple read. The goal is one question you can answer with more confidence: what direction actually feels like me, not just what sounds safe?',
    imageSrc: IMG.credibilityStudent,
    imageAlt: 'Research and discovery in a learning environment',
  },
  school: {
    badge: 'Research shaped model, leadership ready language',
    title: 'Parents and boards ask for proof. Students still need empathy.',
    body:
      'The same Career DNA model scales from a single pilot batch to a whole school rhythm. Leadership sees comparable data, counsellors get repeatable workflows, and families receive PDFs that explain why a recommendation exists, not only what to pick.',
    imageSrc: IMG.credibilitySchool,
    imageAlt: 'Students engaged in structured learning with educator support',
  },
};

export const solutionSectionContent = {
  student: {
    imageSrc: IMG.solutionStudent,
    imageAlt: 'Student working with confidence on a laptop',
    headline: 'Career DNA and AI translate your answers into',
    headlineGradient: 'a story you can actually use',
    ctaLabel: 'Start my Career DNA flow',
    bullets: [
      'Structured Career DNA model with seven traits, not a random personality quiz',
      'Shows how you think, work, and decide, with AI plus expert shaped logic',
      'Clear personalised Career DNA report as a simple PDF you can keep',
      'Surfaces paths that match how you operate, not only the famous careers',
      'Builds confidence because your next step feels explained, not guessed',
    ],
    lead:
      'blends research shaped guidance with AI so your report feels specific to you, calm to read with family, and useful when you pick streams or explore options.',
  },
  school: {
    imageSrc: IMG.solutionSchool,
    imageAlt: 'School leaders reviewing programs and student outcomes',
    headline: 'One Career DNA spine for your school,',
    headlineGradient: 'with AI plus expert shaped reporting',
    ctaLabel: 'Plan our rollout together',
    bullets: [
      'Deploy the same Career DNA framework across whichever grades you prioritise',
      'Bulk assessments that still feel personal thanks to structured PDFs for families',
      'Counsellor workflows you can plug into existing timetables without redoing your entire program',
      'Rollout and partnership support that respects exam cycles and busy leadership calendars',
      'Talking points and artefacts leadership can reuse in board and parent meetings',
    ],
    lead:
      'pairs research shaped Career DNA with tooling for whole school cadence, stretched counsellor teams, and families who want transparency, not buzzwords.',
  },
};

export const howItWorksVisual = {
  student: {
    src: IMG.howStudent,
    alt: 'Open books and learning materials representing your journey',
  },
  school: {
    src: IMG.howSchool,
    alt: 'Organized resources for structured school wide career programs',
  },
};

/** Subtitle under “The ChooseRightCareer Process” */
// export const howItWorksIntroCopy = {
//   student:
//     'Three calm steps, same smart engine underneath. You stay in control the whole time.',
//   school:
//     'Students see a simple flow. Your team gets repeatable reporting, rollout support, and language that holds up in parent and leadership conversations.',
// };

export const schoolProblemPoints = [
  {
    id: 's1',
    title: 'Guidance bandwidth that never quite scales',
    description:
      'Career conversations spike around boards and stream windows, yet few schools can add full time counsellor headcount in every grade.',
    icon: 'Users',
  },
  {
    id: 's2',
    title: 'One framework for the whole school',
    description:
      'When every teacher improvises, families hear mixed signals. A structured Career DNA model keeps insights comparable across batches.',
    icon: 'Building2',
  },
  {
    id: 's3',
    title: 'Proof for parents and leadership',
    description:
      'You need shareable artefacts that show impact, not anecdotes. Clear PDFs and cohort views help you defend the program with calm data.',
    icon: 'TrendingUp',
  },
];
